const API_URL = process.env.REACT_APP_API_URL;

function authHeaders(token) {
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export async function registerUser(email, username, password) {
  try {
    const res = await fetch(`${API_URL}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, username, password }),
    });
    return await res.json();
  } catch (err) {
    return { error: 'Network error — could not reach server.' };
  }
}

export async function loginUser(username, password) {
  try {
    const res = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    return await res.json();
  } catch (err) {
    return { error: 'Network error — could not reach server.' };
  }
}

// ── Account ──────────────────────────────────────────────────────────────────

export async function changePassword(token, currentPassword, newPassword) {
  // Backend needs: POST /account/change-password { currentPassword, newPassword }
  const res = await fetch(`${API_URL}/account/change-password`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return res.json();
}

export async function updateUsername(token, username) {
  // Backend needs: PATCH /account/username { username }
  const res = await fetch(`${API_URL}/account/username`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ username }),
  });
  return res.json();
}

// ── Wallet ───────────────────────────────────────────────────────────────────
// All wallet endpoints are stubs — wire to Railway backend when Stripe is ready.
// Recommended: Stripe Connect (platform escrow model) for skill-gaming wagering.
//   - Players deposit → balance held by platform (Stripe Connect account)
//   - Game entry fee deducted from both players at match start
//   - Winner's balance credited instantly on game_over
//   - Withdrawals via Stripe Connect payouts to bank/card

export async function getWalletBalance(token) {
  // Backend needs: GET /wallet/balance → { balanceCents: number, currency: "usd" }
  const res = await fetch(`${API_URL}/wallet/balance`, { headers: authHeaders(token) });
  return res.json();
}

export async function getWalletTransactions(token) {
  // Backend needs: GET /wallet/transactions → [{ id, type, amountCents, description, createdAt }]
  const res = await fetch(`${API_URL}/wallet/transactions`, { headers: authHeaders(token) });
  return res.json();
}

export async function initiateDeposit(token, amountCents) {
  const res = await fetch(`${API_URL}/wallet/deposit`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ amountCents }),
  });
  return res.json();
}

export async function initiateWithdraw(token, amountCents) {
  const res = await fetch(`${API_URL}/wallet/withdraw`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ amountCents }),
  });
  return res.json();
}

// ── Stripe ────────────────────────────────────────────────────────────────────

export async function createCheckoutSession(token, itemId, itemType) {
  const res = await fetch(`${API_URL}/stripe/create-checkout-session`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ itemId, itemType }),
  });
  return res.json(); // { url }
}

export async function createSubscriptionSession(token) {
  const res = await fetch(`${API_URL}/stripe/create-subscription`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return res.json(); // { url }
}

export async function getUserPurchases(token) {
  const res = await fetch(`${API_URL}/user/purchases`, {
    headers: authHeaders(token),
  });
  return res.json(); // { purchases: [{ itemId, itemType }], subscription: { status, current_period_end } | null }
}

export async function getGameMoves(token, gameId) {
  const res = await fetch(`${API_URL}/games/${gameId}/moves`, {
    headers: authHeaders(token),
  });
  return res.json(); // { moves, whitePlayer, blackPlayer, winner }
}

export async function getUserGames(token) {
  const res = await fetch(`${API_URL}/user/games`, {
    headers: authHeaders(token),
  });
  return res.json(); // [{ id, opponent, playedAs, result, winner, moveCount, gameType, playedAt }]
}

export async function getConnectOnboardUrl(token) {
  const res = await fetch(`${API_URL}/stripe/connect/onboard`, {
    headers: authHeaders(token),
  });
  return res.json(); // { url }
}

export async function claimMonthlyDrop(token) {
  const res = await fetch(`${API_URL}/arena/claim-monthly-drop`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (res.status === 401) return { error: 'Session expired — please sign out and sign back in.' };
  return res.json();
}

export async function redeemPromo(token, code) {
  const res = await fetch(`${API_URL}/promo/redeem`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ code }),
  });
  if (res.status === 401) return { error: 'Session expired — please sign out and sign back in.' };
  return res.json();
}

export async function buyCrowns(token, bundleId) {
  const res = await fetch(`${API_URL}/crowns/purchase`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ bundleId }),
  });
  return res.json(); // { url }
}

export async function saveGameResult(token, gameData) {
  const res = await fetch(`${API_URL}/games/save-result`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(gameData),
  });
  return res.json();
}

export async function analyzeGame(token, pgn, playerColor) {
  try {
    const res = await fetch(`${API_URL}/blanche/analyze`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ pgn, ...(playerColor ? { playerColor } : {}) }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data?.error || `Server error ${res.status}` };
    return data;
  } catch { return null; }
}

export async function narrateMove(token, pgn, lastMove) {
  const res = await fetch(`${API_URL}/blanche/narrate`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ pgn, lastMove }),
  });
  return res.json();
}

export async function createTrialSession(token) {
  const res = await fetch(`${API_URL}/stripe/create-trial`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  return res.json();
}

export async function getConnectStatus(token) {
  const res = await fetch(`${API_URL}/stripe/connect/status`, {
    headers: authHeaders(token),
  });
  return res.json(); // { connected, payoutsEnabled, chargesEnabled }
}

// ── Crown cosmetics ───────────────────────────────────────────────────────────

export async function getOwnedCosmetics(token) {
  const res = await fetch(`${API_URL}/cosmetics/owned`, {
    headers: authHeaders(token),
  });
  return res.json(); // [{ itemId, itemType, crownSpent, createdAt }]
}

// ── Elo / Leaderboard ─────────────────────────────────────────────────────────

export async function getLeaderboard(token) {
  const res = await fetch(`${API_URL}/leaderboard`, { headers: authHeaders(token) });
  return res.json(); // { players: [{rank, username, elo}], userElo, userRank }
}

export async function getUserElo(token) {
  const res = await fetch(`${API_URL}/user/elo`, { headers: authHeaders(token) });
  return res.json(); // { elo, rank, history: [{change, result, createdAt}] }
}

// ── Daily Puzzle ─────────────────────────────────────────────────────────────

export async function getDailyPuzzleStatus(token) {
  const res = await fetch(`${API_URL}/puzzle/daily`, {
    headers: authHeaders(token),
  });
  return res.json(); // { puzzleId, completed, streak, crownsEarned }
}

export async function completePuzzle(token, puzzleId, hintsUsed) {
  const res = await fetch(`${API_URL}/puzzle/complete`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ puzzleId, hintsUsed }),
  });
  return res.json(); // { ok, crowns, streak, bonus }
}

export async function purchaseShopItemWithCrowns(token, itemId, itemType) {
  const res = await fetch(`${API_URL}/shop/purchase-with-crowns`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ itemId, itemType }),
  });
  return res.json(); // { ok, crowns } | { error }
}

export async function purchaseCosmeticWithCrowns(token, itemId) {
  const res = await fetch(`${API_URL}/cosmetics/purchase`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ itemId }),
  });
  return res.json(); // { ok, crowns } | { error }
}